import { useTranslation } from 'next-i18next';
import { MouseEvent, MouseEventHandler, ReactNode } from 'react';
import styled from 'styled-components';
import ClipboardIcon from '../../../../new-components/svg-icons/Clipboard';
import NewFavoriteIcon from '../../../../new-components/svg-icons/NewFavoriteIcon';
import { useDispatch, useSelector } from 'react-redux';
import { getCompareListIds } from '../../../compare/helpers';
import { handleCardBtnClick } from '../../../../helpers/cards';
import {
  hideCompareTooltip,
  updateCompareItems
} from '../../../../redux/slices/compareSlice';
import { EntityTypes } from '../../../../new-lib/constants/common';
import { useRouter } from 'next/router';
import { toast } from 'react-toastify';
import Cookies from 'universal-cookie';
import {
  checkIsFavorite,
  handleClickTheFavorite
} from '../../../../helpers/favorites';
import { Area, Compound } from '../../dtos';

interface ActionButtonProps {
  children: ReactNode;
  handleClick: MouseEventHandler<HTMLButtonElement> | undefined;
  isActive?: boolean;
}

const ActionButton = ({
  children,
  handleClick,
  isActive
}: ActionButtonProps) => {
  return (
    <ActionButtonContainer onClick={handleClick} isActive={isActive}>
      {children}
    </ActionButtonContainer>
  );
};

interface CardActionsProps {
  id: number;
  slug: string;
  index: number;
  price: number;
  recommended: boolean;
  compound?: Compound;
  area?: Area;
  entityType: EntityTypes;
}

const CardActions = ({
  id,
  slug,
  index,
  price,
  recommended,
  compound,
  area,
  entityType
}: CardActionsProps) => {
  const { t } = useTranslation(['search', 'common']);
  const { locale } = useRouter();

  const isMobile = useSelector((state: any) => state.app.isMobile);

  const favorites = useSelector(
    (state: any) => state.favorites[`${entityType}Favorites`]
  );

  const showStartCompare = useSelector(
    (state: any) => state.compare.show_compare_tooltip
  );

  const compareListIds = useSelector(state =>
    getCompareListIds(state, entityType)
  );

  const dispatch = useDispatch();

  const entityIsInCompareList = () => {
    return compareListIds.includes(id);
  };

  const handleCompareClick = (e: MouseEvent) => {
    handleCardBtnClick(
      e,
      { featureName: 'compare' },
      entityIsInCompareList() ? 'cmpr_remove' : 'cmpr_add',
      id,
      price
    );
    dispatch(
      updateCompareItems({
        compare_type:
          entityType === EntityTypes.COMPOUND ? 'compounds' : 'properties',
        item_id: id,
        isMobile,
        fireEvent: false
      })
    );
    dispatch(hideCompareTooltip());
  };

  const handleCopyClick = (e: MouseEvent) => {
    const cardUrl = `${process.env.NEXT_PUBLIC_HOST}/${
      locale === 'ar' ? 'ar/' : ''
    }${
      entityType === EntityTypes.COMPOUND
        ? `compound/${slug}`
        : `compound/${compound?.slug}/property/${slug}`
    }`;

    handleCardBtnClick(
      e,
      { cardPosition: index + 1 },
      'share_click',
      id,
      price
    );

    navigator.clipboard.writeText(cardUrl);
    toast.info(t('copied_to_clipboard', { ns: 'common' }));
  };

  const checkIsFavorite = () => {
    return favorites.some((favoriteId: number) => favoriteId === id);
  };

  const handleFavoriteClick = (e: MouseEvent) => {
    handleCardBtnClick(
      e,
      { featureName: 'favorite' },
      checkIsFavorite() ? 'fav_remove' : 'fav_add',
      id,
      price
    );

    const payload = {
      client_id: new Cookies().get('clientID', { doNotParse: true }),
      entity_id: id
    };

    handleClickTheFavorite(checkIsFavorite(), entityType, dispatch, payload);
  };

  return (
    <Container>
      <ActionButton
        handleClick={handleCompareClick}
        isActive={entityIsInCompareList()}
      >
        <span>{t('compare', { ns: 'common' })}</span>
      </ActionButton>

      <ActionButton handleClick={handleCopyClick}>
        <ClipboardIcon />
      </ActionButton>

      <ActionButton handleClick={handleFavoriteClick}>
        <NewFavoriteIcon
          fill={checkIsFavorite() ? 'red' : 'transparent'}
          stroke={checkIsFavorite() ? 'red' : '#828FA1'}
        />
      </ActionButton>
    </Container>
  );
};

const Container = styled.div`
  position: absolute;
  top: 8px;
  inset-inline-end: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ActionButtonContainer = styled.button<any>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  padding: 8px;
  border-radius: 8px;
  background: ${props => (props.isActive ? '#015c9a' : '#fbfcfe')};
  border: none;
  color: ${props => (props.isActive ? '#fff' : '#5b6573')};
  font-size: 12px;
  font-family: materialRegular;
`;

export default CardActions;
